import {
	GD_SUBSCRIPTION_LOOKBACK_MONTHS,
	GD_SUBSCRIPTION_WINDOW_LABELS,
	gdSubscriberWhere,
	type GdSubscriptionFilterState,
	type GdSubscriptionWindow,
	gdSubscriptionWindow,
	gdSubscriptionWindowStart,
	mergeGdSubscriptionWindows,
	normalizeIco,
} from '@app/lib/gd'
import type { EntityAccessor, EnumFilterArtifact, FilterArtifact, FilterHandler } from '@contember/bindx'
import { ColumnLeaf, type ColumnLeafProps, useDataViewFilter } from '@contember/bindx-dataview'
import { useEntityList } from '@contember/bindx-react'
import { CheckIcon } from 'lucide-react'
import {
	createContext,
	createElement,
	type ReactElement,
	type ReactNode,
	type SetStateAction,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
import { gdSubscriberEntity } from '../admin-schema'
import { formatCzechDate } from '../lib/format-date'
import { pragueToday } from './gd-subscriber-state'
import { Badge } from './status-badge'

/**
 * „Byl tenhle zákazník kurzu v posledních 12 měsících předplatitelem Grantového diáře?"
 *
 * Kurzy a GD spolu v datech nesouvisí — jediná spojnice je IČO na faktuře
 * (`Order.billingIc` ⇄ `GdSubscriber.ico`), takže odpověď nejde přifiltrovat k řádku,
 * musí se doptat zvlášť. Pravidlo samo (co znamená „byl") sedí v `@app/lib/gd`
 * ({@link gdSubscriptionWindow}), aby badge v adminu a sloupec ve staženém CSV
 * (worker `attendance-export-route.ts`) říkaly totéž.
 *
 * Proto tenhle provider (mountovaný jednou v `routes/_layout.tsx`): buňky si o IČO řeknou
 * samy ({@link useGdSubscription}), provider je posbírá a pošle jeden dotaz `ico in (…)`
 * za celou stránku. Bez toho by výpis objednávek posílal jeden dotaz na řádek. Registrace
 * je počítaná — když se grid přepne na další stránku, IČO starých řádků z dotazu vypadnou.
 */

interface GdSubscriptionLookup {
	/** Vrací odhlašovací callback — buňka ho volá při odmountování. */
	register(ico: string): () => void
	/** IČO, na která už odpověď dorazila. Cokoli jiného se ještě načítá. */
	answered: ReadonlySet<string>
	windows: ReadonlyMap<string, GdSubscriptionWindow>
	isError: boolean
}

const GdSubscriptionContext = createContext<GdSubscriptionLookup | null>(null)

const EMPTY_WINDOWS: ReadonlyMap<string, GdSubscriptionWindow> = new Map()
const NO_SUBSCRIPTION: GdSubscriptionWindow = { state: 'none', date: null }

interface GdSubscriptionAnswer {
	/** IČO, na která se dotaz ptal — odpověď platí jen pro ně. */
	icos: readonly string[]
	windows: ReadonlyMap<string, GdSubscriptionWindow>
	isError: boolean
}

const NO_ANSWER: GdSubscriptionAnswer = { icos: [], windows: EMPTY_WINDOWS, isError: false }

/**
 * Sedí v `routes/_layout.tsx`, takže {@link GdSubscriptionCell} funguje na kterékoli
 * stránce adminu. Dokud si žádná buňka o IČO neřekne, neposílá vůbec nic — dotaz vzniká
 * teprve s {@link GdSubscriberProbe}.
 */
export function GdSubscriptionProvider({ children }: { children: ReactNode }): ReactElement {
	const [icos, setIcos] = useState<readonly string[]>([])
	const [answer, setAnswer] = useState<GdSubscriptionAnswer>(NO_ANSWER)
	const counts = useRef<Map<string, number>>(new Map())
	const flushScheduled = useRef(false)

	// Registrace chodí z efektů jednotlivých buněk, tedy po jedné. Sloučíme je do jednoho
	// setState v mikrotasku, jinak by každý řádek tabulky znamenal vlastní přerenderování
	// (a s ním nový dotaz).
	const flush = useCallback(() => {
		if (flushScheduled.current) return
		flushScheduled.current = true
		queueMicrotask(() => {
			flushScheduled.current = false
			const next = [...counts.current.keys()].sort()
			setIcos(prev => prev.length === next.length && prev.every((ico, index) => ico === next[index]) ? prev : next)
		})
	}, [])

	const register = useCallback((ico: string) => {
		counts.current.set(ico, (counts.current.get(ico) ?? 0) + 1)
		flush()
		return () => {
			const remaining = (counts.current.get(ico) ?? 0) - 1
			if (remaining > 0) counts.current.set(ico, remaining)
			else counts.current.delete(ico)
			flush()
		}
	}, [flush])

	const lookup = useMemo<GdSubscriptionLookup>(() => ({
		register,
		answered: new Set(answer.icos),
		windows: answer.windows,
		isError: answer.isError,
	}), [register, answer])

	return (
		<GdSubscriptionContext.Provider value={lookup}>
			{icos.length > 0 ? <GdSubscriberProbe icos={icos} onAnswer={setAnswer} /> : null}
			{children}
		</GdSubscriptionContext.Provider>
	)
}

/**
 * Jediný dotaz za celou stránku, `ico in (…)`. Je to samostatná komponenta, aby ho šlo
 * nemountovat — `useEntityList` se nedá volat podmíněně a prázdný dotaz by stránka bez
 * jediného IČO posílala zbytečně.
 */
function GdSubscriberProbe(
	{ icos, onAnswer }: { icos: readonly string[]; onAnswer: (answer: GdSubscriptionAnswer) => void },
): null {
	const filter = useMemo(() => ({ ico: { in: icos } }), [icos])
	const subscribers = useEntityList(gdSubscriberEntity, { filter }, s => s.ico().orders(o => o.status().periodStart().periodEnd()))

	const windows = useMemo(() => {
		if (subscribers.$isLoading || subscribers.$isError) return EMPTY_WINDOWS
		const today = pragueToday()
		const since = gdSubscriptionWindowStart(today)
		const byIco = new Map<string, GdSubscriptionWindow>()
		for (const subscriber of subscribers.items) {
			const ico = normalizeIco(subscriber.ico.value)
			if (ico === null) continue
			const window = gdSubscriptionWindow(
				subscriber.orders.items.map(order => ({
					status: order.status.value,
					periodStart: order.periodStart.value,
					periodEnd: order.periodEnd.value,
				})),
				{ today, since },
			)
			// Stejné IČO může mít po migraci víc záznamů — platí ten nejživější.
			byIco.set(ico, mergeGdSubscriptionWindows([byIco.get(ico) ?? NO_SUBSCRIPTION, window]))
		}
		return byIco
	}, [subscribers])

	// Hlásí se až s hotovou odpovědí. Kdyby hlásil i rozpracovaný stav, buňka by mezi
	// „ptám se" a „mám" na okamžik ukázala pomlčku, tedy „v GD nic nemá".
	const isLoading = subscribers.$isLoading
	const isError = subscribers.$isError
	useEffect(() => {
		if (isLoading) return
		onAnswer({ icos, windows, isError })
	}, [onAnswer, icos, windows, isLoading, isError])

	return null
}

/**
 * Kolik IČO se z GD nanejvýš vytáhne, když se výpis filtruje podle předplatného. Filtr se
 * skládá ze dvou kol (IČO odběratelů v daném stavu → `billingIc in (…)` nad objednávkami),
 * takže první kolo musí mít strop; při jeho dosažení se to řekne nahlas místo tichého
 * ořezání výsledku.
 */
export const GD_ICO_FILTER_LIMIT = 5000

interface GdIcoAnswer {
	icos: readonly string[]
	isTruncated: boolean
}

/**
 * Výsledky prvního kola, klíčované stavem a dnem. Musí to být modulová proměnná, ne stav
 * komponenty: `toWhere` filtru je synchronní funkce, která nemá kde držet React stav —
 * dotaz proto proběhne vedle a where klauzule si sáhne sem. Až se odpověď uloží, mřížka se
 * překreslí přes `reload()`.
 */
const icoCache = new Map<string, GdIcoAnswer>()

const icoCacheKey = (state: GdSubscriptionFilterState, today: string) => `${state}|${today}`

function useGdIcos(state: GdSubscriptionFilterState): { answer: GdIcoAnswer | null; isLoading: boolean; isError: boolean } {
	const filter = useMemo(() => {
		const today = pragueToday()
		return gdSubscriberWhere(state, { today, since: gdSubscriptionWindowStart(today) })
	}, [state])

	const subscribers = useEntityList(gdSubscriberEntity, { filter, limit: GD_ICO_FILTER_LIMIT }, s => s.ico())

	return useMemo(() => {
		if (subscribers.$isLoading) return { answer: null, isLoading: true, isError: false }
		if (subscribers.$isError) return { answer: null, isLoading: false, isError: true }

		const icos = [...new Set(subscribers.items.map(subscriber => normalizeIco(subscriber.ico.value)).filter(ico => ico !== null))]
		return { answer: { icos, isTruncated: subscribers.items.length >= GD_ICO_FILTER_LIMIT }, isLoading: false, isError: false }
	}, [subscribers])
}

export type GdSubscriptionStatus =
	| { status: 'no-ico' }
	| { status: 'pending' }
	| { status: 'error' }
	| { status: 'ready'; window: GdSubscriptionWindow }

/** Stav předplatného pro jedno IČO. Musí běžet uvnitř {@link GdSubscriptionProvider}. */
export function useGdSubscription(ico: string | null | undefined): GdSubscriptionStatus {
	const lookup = useContext(GdSubscriptionContext)
	const key = normalizeIco(ico)
	// Registrace stojí před kontrolou provideru, aby pořadí hooků nezáviselo na kontextu.
	const register = lookup?.register
	useEffect(() => {
		if (register === undefined || key === null) return
		return register(key)
	}, [register, key])

	if (lookup === null) {
		throw new Error('useGdSubscription musí být uvnitř <GdSubscriptionProvider> — bez něj se nemá kdo doptat GD na IČO.')
	}
	if (key === null) return { status: 'no-ico' }
	if (lookup.isError) return { status: 'error' }
	// Dokud na tohle IČO odpověď nedorazila, „nenašli jsme" by byla lež — buňka se
	// zaregistrovala až po dotazu a ten její teprve poletí.
	if (!lookup.answered.has(key)) return { status: 'pending' }
	return { status: 'ready', window: lookup.windows.get(key) ?? NO_SUBSCRIPTION }
}

const WINDOW_TONE = {
	active: 'success',
	lapsed: 'rose',
	upcoming: 'info',
	none: 'neutral',
} as const

/**
 * Buňka sloupce „GD předplatné". Kontext („GD") nese hlavička sloupce, badge říká jen stav;
 * datum pod ním je stejně důležité jako badge sám — bez něj nejde poznat, jestli „Vypršelo"
 * znamená minulý týden, nebo těsně na hraně dvanácti měsíců.
 *
 * `whenEmpty="hide"` je pro místa mimo tabulku (hlavička detailu): tam je pomlčka u údaje,
 * který nikdo nehledal, jen šum — buď je co říct, nebo tam badge vůbec nepatří.
 */
export function GdSubscriptionCell(
	{ ico, layout = 'stacked', whenEmpty = 'dash', label, showDate = true }: {
		ico: string | null | undefined
		layout?: 'stacked' | 'inline'
		whenEmpty?: 'dash' | 'hide'
		/**
		 * Předpona dovnitř badge — „GD vypršelo" místo holého „Vypršelo". Pro místa mimo tabulku,
		 * kde kontext nedává hlavička sloupce; samostatný popisek vedle badge by tam po skrytí
		 * badge zůstal viset sám.
		 */
		label?: string
		/**
		 * Datum pod/za badgem. V tabulce je nezbytné („Vypršelo" minulý týden vs. na hraně roku),
		 * v hlavičce detailu ne — tam je totéž o kus níž v oddílu Fakturace.
		 */
		showDate?: boolean
	},
): ReactElement | null {
	const state = useGdSubscription(ico)
	const hasNothingToSay = state.status !== 'ready' || state.window.state === 'none'
	if (whenEmpty === 'hide' && hasNothingToSay) return null

	if (state.status === 'no-ico') {
		return <span className="text-gray/50 italic" title="Objednávka nemá IČO (fyzická osoba) — není podle čeho spárovat.">—</span>
	}
	if (state.status === 'pending') return <span className="text-gray/50">…</span>
	if (state.status === 'error') {
		return <span className="text-gray/50 italic" title="Nepodařilo se načíst odběratele GD.">?</span>
	}
	if (state.window.state === 'none') {
		return (
			<span
				className="text-gray/50 italic"
				title={`V Grantovém diáři není pod IČO ${normalizeIco(ico)} zaplacené předplatné za posledních ${GD_SUBSCRIPTION_LOOKBACK_MONTHS} měsíců.`}
			>
				—
			</span>
		)
	}

	const { state: windowState, date } = state.window
	const note = date === null ? null : windowState === 'active'
		? `do ${formatCzechDate(date)}`
		: windowState === 'upcoming'
		? `od ${formatCzechDate(date)}`
		: formatCzechDate(date)

	return (
		<span className={layout === 'stacked' ? 'inline-flex flex-col items-start gap-0.5' : 'inline-flex items-center gap-2'}>
			<Badge tone={WINDOW_TONE[windowState]}>
				{label ? `${label} ${GD_SUBSCRIPTION_WINDOW_LABELS[windowState].toLocaleLowerCase('cs')}` : GD_SUBSCRIPTION_WINDOW_LABELS[windowState]}
			</Badge>
			{showDate && note
				? (
					<span className={`whitespace-nowrap tabular-nums ${layout === 'stacked' ? 'text-[11px] text-gray' : 'text-sm text-blue-primary'}`}>
						{showDate && note}
					</span>
				)
				: null}
		</span>
	)
}

/**
 * Sloupec „GD předplatné" pro mřížku objednávek — buňka i filtr v hlavičce, jako u ostatních
 * sloupců.
 *
 * Vlastní `ColumnLeaf` místo některého ze stock sloupců proto, že tohle není pole: je to
 * otázka na jinou databázi. Sloupec proto ani nejde řadit (Postgres tu hodnotu nezná) a filtr
 * se skládá ze dvou kol — nejdřív IČO odběratelů GD v daném stavu ({@link useGdIcos}), pak
 * `billingIc in (…)` nad objednávkami. Proto ta modulová keš: {@link gdSubscriptionFilterWhere}
 * musí odpovědět synchronně, a {@link GdSubscriptionFilterLoader} ji doplní a mřížku překreslí.
 *
 * Filtrovat jde jen na „Aktivní" a „Vypršelo": „nic v GD nemá" by znamenalo hledat objednávky
 * podle nepřítomnosti v druhé databázi, což jedním `in` napsat nejde.
 */
export function GdSubscriptionColumn(_props: { header?: ReactNode }): null {
	return null
}

const GD_FILTER_NAME = 'gdSubscription'

/** IČO, pod kterými se nenajde nic — dokud první kolo neodpoví, ať výpis raději mlčí, než aby lhal. */
const NO_ICO_MATCH = { billingIc: { in: [] as string[] } }

function gdSubscriptionFilterWhere(artifact: EnumFilterArtifact | undefined): Record<string, unknown> | undefined {
	const wanted = artifact?.values?.[0]
	if (wanted !== 'active' && wanted !== 'lapsed') return undefined
	const answer = icoCache.get(icoCacheKey(wanted, pragueToday()))
	return answer === undefined ? NO_ICO_MATCH : { billingIc: { in: answer.icos } }
}

const gdSubscriptionFilterHandler: FilterHandler<EnumFilterArtifact> = {
	defaultArtifact: () => ({}),
	// Artefakt může chybět úplně: mřížka si ukládá celý filtr objektem a vrací ho jako
	// `stored ?? defaults`, bez slučování — viz stejná past u `SubscriberStateColumn`.
	isActive: artifact => (artifact?.values?.length ?? 0) > 0,
	toWhere: artifact => gdSubscriptionFilterWhere(artifact),
}

const GD_FILTER_OPTIONS: Array<{ value: GdSubscriptionFilterState; label: string; tone: 'success' | 'rose' }> = [
	{ value: 'active', label: 'Aktivní', tone: 'success' },
	{ value: 'lapsed', label: 'Vypršelo', tone: 'rose' },
]

GdSubscriptionColumn.staticRender = (props: Record<string, unknown>): ReactNode => {
	const leafProps: ColumnLeafProps = {
		columnType: 'action',
		name: GD_FILTER_NAME,
		fieldName: null,
		fieldRef: null,
		sortingField: null,
		filterName: GD_FILTER_NAME,
		filterHandler: gdSubscriptionFilterHandler as FilterHandler<FilterArtifact>,
		isTextSearchable: false,
		header: (props.header as ReactNode) ?? 'GD předplatné',
		// Buňka čte IČO řádku, ale sama na žádném poli nestojí — bez tohohle by po skrytí
		// sloupce „IČO" četla nenačtené pole a bindx by celou mřížku shodil.
		collectSelection: collector => {
			void (collector as EntityAccessor<{ billingIc: string }>).billingIc.value
		},
		renderCell: accessor => createElement(GdSubscriptionCell, { ico: (accessor as EntityAccessor<{ billingIc: string }>).billingIc.value }),
		renderFilter: ({ artifact, setArtifact }) =>
			createElement(GdSubscriptionFilterControls, {
				artifact: artifact as EnumFilterArtifact | undefined,
				setArtifact: setArtifact as (next: EnumFilterArtifact) => void,
			}),
	}

	return createElement(ColumnLeaf, leafProps)
}

/** Zapnutý stav se kliknutím zase vypne — stejné gesto tam i zpět. */
function GdSubscriptionFilterControls(
	{ artifact, setArtifact }: { artifact: EnumFilterArtifact | undefined; setArtifact: (next: EnumFilterArtifact) => void },
): ReactElement {
	const current = artifact?.values?.[0]
	const answer = current === 'active' || current === 'lapsed' ? icoCache.get(icoCacheKey(current, pragueToday())) : undefined

	return (
		<div className="flex flex-col gap-1">
			{GD_FILTER_OPTIONS.map(option => (
				<button
					className="flex items-center justify-between gap-3 rounded-sm px-2 py-1.5 text-left hover:bg-light-gray"
					key={option.value}
					onClick={() => setArtifact(current === option.value ? {} : { values: [option.value] })}
					type="button"
				>
					<Badge tone={option.tone}>{option.label}</Badge>
					{current === option.value ? <CheckIcon className="size-3.5 text-blue-primary" /> : null}
				</button>
			))}
			{current
				? (
					<p className="px-2 pt-1 text-[11px] leading-snug text-gray">
						{answer === undefined
							? 'Hledám odběratele v Grantovém diáři…'
							: answer.isTruncated
							? `Přes ${GD_ICO_FILTER_LIMIT} odběratelů GD — výpis může být neúplný.`
							: `Párováno podle IČO ${answer.icos.length === 1 ? '1 odběratele' : `${answer.icos.length} odběratelů`} GD.`}
					</p>
				)
				: null}
		</div>
	)
}

/**
 * Doplňuje keš prvního kola. Patří dovnitř mřížky (mezi sloupce v `orders.tsx`), aby viděl
 * na její filtr; renderuje se jako nic. Bez aktivního filtru neposílá žádný dotaz.
 */
export function GdSubscriptionFilterLoader(
	{ onStateChange }: { onStateChange?: (state: GdSubscriptionFilterState | null) => void },
): ReactElement | null {
	const [artifact, setArtifact] = useDataViewFilter<EnumFilterArtifact>(GD_FILTER_NAME)
	const value = artifact?.values?.[0]
	const state = value === 'active' || value === 'lapsed' ? value : null

	// Export CSV visí v hlavičce stránky, tedy mimo mřížku — a „Export respektuje aktivní
	// filtr" by bez tohohle přestalo platit ve chvíli, kdy se filtruje podle předplatného.
	useEffect(() => {
		onStateChange?.(state)
	}, [onStateChange, state])

	if (state === null || icoCache.has(icoCacheKey(state, pragueToday()))) return null
	return <GdSubscriptionIcoLoader setArtifact={setArtifact} state={state} />
}

function GdSubscriptionIcoLoader(
	{ state, setArtifact }: {
		state: GdSubscriptionFilterState
		setArtifact: (next: SetStateAction<EnumFilterArtifact | undefined>) => void
	},
): null {
	const { answer } = useGdIcos(state)

	useEffect(() => {
		if (answer === null) return
		icoCache.set(icoCacheKey(state, pragueToday()), answer)
		// Where klauzule se počítá z artefaktu filtru, ne z Reactu — samotné doplnění keše
		// by mřížka nezaznamenala a zůstala by u „nic nenalezeno". Razítko v artefaktu je
		// ta nejmenší změna, kterou si všimne a dotaz přepočítá; podruhé se sem nedostaneme,
		// protože keš je od té chvíle plná.
		setArtifact(previous => ({ ...previous, values: previous?.values ?? [state], resolvedAt: Date.now() }))
	}, [answer, state, setArtifact])

	return null
}
